import { Dispatch, SetStateAction, useEffect, useState, useCallback } from "react";
import { GroupUserRecord } from "models/view-models/groups/group-user-record";
import { GroupUsersService } from "utilities/services/groups/group-users-service";
import { TranslatedCopy } from "utilities/interfaces/culture-resources";
import { t } from "utilities/localization/t";
import { ToastManager } from "utilities/toast/toast-manager";
import { useErrorMessageState } from "utilities/hooks/use-error-state";
import { CreateBulkGroupUsersDtoRecord } from "../../../../models/view-models/groups/create-bulk-group-users-params-record";
import { ResultRecord } from "andculturecode-javascript-core";
import { BulkGroupUsersSummaryRecord } from "../../../../models/view-models/groups/bulk-group-users-summary-record";
import { EncodingType } from "models/enumerations/encoding/encoding-type";
import { FileUtils } from "utilities/files/file-utils";

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const ERROR_LOADING: TranslatedCopy = "thereWasAnIssueLoadingGroupUsers";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UseGroupUsersHook {
    error?: Error;
    isLoading: boolean;
    groupUsers: GroupUserRecord[];
    rowCount: number;
    setRefresh: Dispatch<SetStateAction<boolean>>;
    deleteGroupUser: (groupUser: GroupUserRecord) => Promise<boolean>;
    createBulkGroupUsers: (
        createBulkGroupUsersDto: CreateBulkGroupUsersDtoRecord
    ) => Promise<ResultRecord<BulkGroupUsersSummaryRecord>>;
}

interface UseGroupUsersHookOptions {
    onError?: (error: Error) => void;
    groupId: number;
    searchCriteriaReady?: boolean;
    searchText?: string;
    skip?: number;
    take?: number;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Hook
// -------------------------------------------------------------------------------------------------

export function UseGroupUsers({
    onError,
    groupId,
    searchText,
    skip,
    take,
    searchCriteriaReady = true,
}: UseGroupUsersHookOptions): UseGroupUsersHook {
    const encodingType: EncodingType = FileUtils.getEncodingType();
    const { list } = GroupUsersService.useList();
    const { delete: deleteApi } = GroupUsersService.useDelete();
    const [groupUsers, setGroupUsers] = useState<GroupUserRecord[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [error, setError] = useErrorMessageState({ onError });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [refresh, setRefresh] = useState(false);
    const { create: bulkGroupUsersCreate } = GroupUsersService.useBulkCreate();

    const fetchGroupUsers = useCallback(async (): Promise<void> => {
        if (!searchCriteriaReady) {
            setGroupUsers([]);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const groupUsersResponse = await list({
                groupId: groupId,
                searchText: searchText,
                skip: skip,
                take: take,
            });
            setIsLoading(false);

            if (
                groupUsersResponse?.resultObjects == null ||
                groupUsersResponse?.results == null ||
                groupUsersResponse.results.hasErrors()
            ) {
                setError(t(ERROR_LOADING));
                return;
            }

            setGroupUsers(groupUsersResponse.resultObjects);
            setRowCount(groupUsersResponse.rowCount);
        } catch {
            setError(t(ERROR_LOADING));
        }
    }, [groupId, list, searchCriteriaReady, searchText, setError, skip, take]);

    useEffect(() => {
        fetchGroupUsers();
    }, [fetchGroupUsers]);

    const deleteGroupUser = async (groupUser: GroupUserRecord): Promise<boolean> => {
        try {
            if (groupUser.id == null) {
                return false;
            }

            const deleteResponse = await deleteApi(groupUser.id);

            if (!deleteResponse) {
                throw new Error("");
            }

            fetchGroupUsers();

            ToastManager.success(t("userRemovedSuccessfully"));
            return true;
        } catch {
            ToastManager.error(t("thereWasAnIssueRemovingTheUser"));
            return false;
        }
    };
    useEffect(() => {
        if (refresh) {
            fetchGroupUsers();
            setRefresh(false);
        }
    }, [fetchGroupUsers, refresh]);

    const createBulkGroupUsers = async (
        createBulkGroupUsersDto: CreateBulkGroupUsersDtoRecord
    ): Promise<ResultRecord<BulkGroupUsersSummaryRecord>> => {
        let result = new ResultRecord<BulkGroupUsersSummaryRecord>();

        if (createBulkGroupUsersDto === undefined) {
            result.addError("createBulkGroupUsersDto", t("bulkGroupUsersFileAreNeeded"));
            return result;
        }

        try {
            const createBulkEnrollmentResponse = await bulkGroupUsersCreate(
                encodingType,
                createBulkGroupUsersDto
            );

            const createBulkEnrollmentResult = createBulkEnrollmentResponse?.result;

            if (createBulkEnrollmentResult?.resultObject == null) {
                throw new Error();
            }

            result = createBulkEnrollmentResult;
        } catch (error) {
            try {
                result = error as ResultRecord<BulkGroupUsersSummaryRecord>;
            } catch (error: any) {
                result.addError("", error.toString());
                ToastManager.error(t("thereWasAProblemSavingBulkGroupUsers"));
            }
        }

        // Retrieve the data.
        fetchGroupUsers();

        return result;
    };

    return {
        error,
        isLoading,
        groupUsers,
        rowCount,
        setRefresh,
        createBulkGroupUsers,
        deleteGroupUser,
    };
}

// #endregion Hook
